import React, { useEffect, useState } from 'react';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import Spacer from '~/components/Spacer/Spacer';
import Spinner from '~/components/Spinner/Spinner';
import {
  AuditKind,
  AuditListResponse,
  getNotificationAudit
} from '~/api/notificationAudit';
import NotificationAuditResultModal, {
  pct,
  sumCounts
} from './NotificationAuditResultModal';
import NotificationAuditBuildingsModal from './NotificationAuditBuildingsModal';
import classes from './NotificationAudit.module.css';

const PAGE_SIZE = 25;
const KINDS: { value: AuditKind; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'single', label: 'Single' },
  { value: 'bulk', label: 'Multi-building' }
];

const NotificationAudit = () => {
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState<AuditKind>('all');
  const [data, setData] = useState<AuditListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openBatch, setOpenBatch] = useState<string | null>(null);
  const [openBuildings, setOpenBuildings] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getNotificationAudit({ page, pageSize: PAGE_SIZE, kind })
      .then(({ data }) => setData(data))
      .catch((e) =>
        setError(
          e?.response?.data?.message || e?.message || 'Could not load posts.'
        )
      )
      .finally(() => setLoading(false));
  }, [page, kind]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const pager = (
    <div className={classes.pager}>
      <span className={classes.subtle}>
        {data ? `${data.total} posts · page ${page} of ${totalPages}` : ''}
      </span>
      <button
        className='btn-standard btn-secondary'
        onClick={() => setPage(page - 1)}
        disabled={page <= 1 || loading}
      >
        Previous
      </button>
      <button
        className='btn-standard btn-secondary'
        onClick={() => setPage(page + 1)}
        disabled={page >= totalPages || loading}
      >
        Next
      </button>
    </div>
  );

  return (
    <div className='content-container'>
      <Breadcrumbs />
      <Spacer height={20} />
      <div className={classes.header}>
        <div>
          <h1>Notification Audit</h1>
          <p className={classes.subtle}>
            Posts pushed to Homebody residents in the last 30 days. Numbers are
            what Entrata reported back per resident.
          </p>
        </div>
        <div className={classes.filters}>
          {KINDS.map((k) => (
            <button
              key={k.value}
              className={`btn-standard btn-secondary ${
                kind === k.value ? classes.filterActive : ''
              }`}
              onClick={() => {
                setKind(k.value);
                setPage(1);
              }}
            >
              {k.label}
            </button>
          ))}
        </div>
      </div>

      {pager}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spinner />
        </div>
      ) : error ? (
        <p className={classes.bad}>{error}</p>
      ) : !data || !data.posts.length ? (
        <p className={classes.subtle}>No posts in this period.</p>
      ) : (
        <div className={classes.tableWrapper}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Posted</th>
                <th>Type</th>
                <th>Building(s)</th>
                <th>Post</th>
                <th className={classes.num}>Sent to partner</th>
                <th className={classes.num}>Delivered</th>
                <th className={classes.num}>Rejected</th>
                <th className={classes.num}>No status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.posts.map((p) => {
                const s = sumCounts(p);
                return (
                  <tr key={p.batchUuid}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {new Date(p.startedAt).toLocaleString()}
                      {p.isBackfilled && (
                        <div className={classes.approx}>approx.</div>
                      )}
                    </td>
                    <td>
                      <span
                        className={`${classes.badge} ${
                          p.isBulk ? classes.badgeBulk : ''
                        }`}
                      >
                        {p.isBulk ? `Multi · ${p.buildingCount}` : 'Single'}
                      </span>
                    </td>
                    <td>
                      {(p.buildingNames || []).slice(0, 3).join(', ') || '—'}
                      {p.buildingCount > 3 && (
                        <>
                          {' '}
                          <button
                            className={classes.linkButton}
                            onClick={() => setOpenBuildings(p.batchUuid)}
                          >
                            +{p.buildingCount - 3} more
                          </button>
                        </>
                      )}
                    </td>
                    <td>
                      <div className={classes.postText}>
                        {p.postText || '—'}
                      </div>
                    </td>
                    <td className={classes.num}>{s.sent}</td>
                    <td className={classes.num}>
                      {s.delivered}{' '}
                      <span className={classes.subtle}>
                        ({pct(s.delivered, s.delivered + s.rejected)})
                      </span>
                    </td>
                    <td className={classes.num}>{s.rejected}</td>
                    <td className={classes.num}>{s.noStatus}</td>
                    <td>
                      <button
                        className='btn-standard btn-primary'
                        onClick={() => setOpenBatch(p.batchUuid)}
                      >
                        View push result
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && data.total > PAGE_SIZE && pager}

      <NotificationAuditBuildingsModal
        batchUuid={openBuildings}
        onClose={() => setOpenBuildings(null)}
      />
      <NotificationAuditResultModal
        batchUuid={openBatch}
        onClose={() => setOpenBatch(null)}
      />
    </div>
  );
};

export default NotificationAudit;
